import { useCallback, useEffect, useRef, useState } from 'react';
import {
  CircleCheck,
  CircleX,
  Info,
  TriangleAlert,
  X,
  type LucideIcon,
} from 'lucide-react';
import type { Toast, ToastType } from '../../../types/toast';
import { TOAST_EXIT_DURATION_MS } from '../../../types/toast';
import './LiksToaster.css';

const TOAST_ICONS: Record<ToastType, LucideIcon> = {
  success: CircleCheck,
  warning: TriangleAlert,
  error: CircleX,
  info: Info,
};

export type LiksToastProps = {
  toast: Toast;
  isExiting: boolean;
  dismissLabel: string;
  onDismiss: () => void;
  onExitComplete: () => void;
};

export function LiksToast({
  toast,
  isExiting,
  dismissLabel,
  onDismiss,
  onExitComplete,
}: LiksToastProps) {
  const startTimeRef = useRef(0);
  const durationMsRef = useRef<number | null>(toast.durationMs);
  const timeoutRef = useRef<number | null>(null);
  const isPausedRef = useRef(false);
  const exitCompletedRef = useRef(false);
  const onDismissRef = useRef(onDismiss);
  const onExitCompleteRef = useRef(onExitComplete);
  const [isPaused, setIsPaused] = useState(false);
  const isPersistent = toast.durationMs === null;

  useEffect(() => {
    onDismissRef.current = onDismiss;
    onExitCompleteRef.current = onExitComplete;
  }, [onDismiss, onExitComplete]);

  const getRemainingMs = useCallback(() => {
    return (durationMsRef.current ?? 0) - (Date.now() - startTimeRef.current);
  }, []);

  const clearTimer = useCallback(() => {
    if (timeoutRef.current !== null) {
      window.clearTimeout(timeoutRef.current);
      timeoutRef.current = null;
    }
  }, []);

  const scheduleDismiss = useCallback(() => {
    if (isPersistent) {
      return;
    }

    clearTimer();

    const remainingMs = getRemainingMs();
    if (remainingMs <= 0) {
      onDismissRef.current();
      return;
    }

    timeoutRef.current = window.setTimeout(() => {
      onDismissRef.current();
    }, remainingMs);
  }, [clearTimer, getRemainingMs, isPersistent]);

  const requestDismiss = useCallback(() => {
    if (isExiting || isPausedRef.current) {
      return;
    }

    clearTimer();
    onDismissRef.current();
  }, [clearTimer, isExiting]);

  const pauseTimer = useCallback(() => {
    if (isPersistent || isPausedRef.current || isExiting) {
      return;
    }

    isPausedRef.current = true;
    setIsPaused(true);
    clearTimer();
    durationMsRef.current = Math.max(0, getRemainingMs());
  }, [clearTimer, getRemainingMs, isExiting, isPersistent]);

  const resumeTimer = useCallback(() => {
    if (isPersistent || !isPausedRef.current || isExiting) {
      return;
    }

    isPausedRef.current = false;
    setIsPaused(false);
    startTimeRef.current = Date.now();
    scheduleDismiss();
  }, [isExiting, isPersistent, scheduleDismiss]);

  const finishExit = useCallback(() => {
    if (exitCompletedRef.current) {
      return;
    }

    exitCompletedRef.current = true;
    onExitCompleteRef.current();
  }, []);

  useEffect(() => {
    if (isPersistent) {
      return undefined;
    }

    startTimeRef.current = Date.now();
    durationMsRef.current = toast.durationMs ?? 0;
    scheduleDismiss();
    return clearTimer;
  }, [toast.id, toast.durationMs, scheduleDismiss, clearTimer, isPersistent]);

  useEffect(() => {
    if (isExiting) {
      isPausedRef.current = false;
      clearTimer();
    }
  }, [isExiting, clearTimer]);

  useEffect(() => {
    if (!isExiting) {
      return;
    }

    const timeout = window.setTimeout(() => {
      finishExit();
    }, TOAST_EXIT_DURATION_MS + 50);

    return () => window.clearTimeout(timeout);
  }, [isExiting, finishExit]);

  const handleToastAnimationEnd = (
    event: React.AnimationEvent<HTMLDivElement>,
  ) => {
    if (
      !isExiting ||
      event.currentTarget !== event.target ||
      event.animationName !== 'liks-toast-exit'
    ) {
      return;
    }

    finishExit();
  };

  const handleProgressAnimationEnd = (
    event: React.AnimationEvent<HTMLDivElement>,
  ) => {
    if (
      event.currentTarget !== event.target ||
      event.animationName !== 'liks-toast-progress'
    ) {
      return;
    }

    requestDismiss();
  };

  const ToastIcon = TOAST_ICONS[toast.type];

  return (
    <div
      className={[
        'liks-toast',
        `liks-toast--${toast.type}`,
        isPersistent && 'liks-toast--persistent',
        isExiting && 'liks-toast--exiting',
        isPaused && 'liks-toast--paused',
      ]
        .filter(Boolean)
        .join(' ')}
      style={
        isPersistent || toast.durationMs == null
          ? undefined
          : ({
              '--toast-duration': `${toast.durationMs}ms`,
            } as React.CSSProperties)
      }
      role={toast.type === 'error' ? 'alert' : 'status'}
      aria-live={toast.type === 'error' ? 'assertive' : 'polite'}
      aria-atomic="true"
      onAnimationEnd={handleToastAnimationEnd}
      onPointerEnter={pauseTimer}
      onPointerLeave={resumeTimer}
      onPointerDown={pauseTimer}
      onPointerUp={resumeTimer}
      onPointerCancel={resumeTimer}
    >
      <ToastIcon className="liks-toast__icon" strokeWidth={2} aria-hidden />

      <div className="liks-toast__content">
        <p className="liks-toast__title">{toast.title}</p>
        {toast.description != null && toast.description !== '' && (
          <p className="liks-toast__description">{toast.description}</p>
        )}
        {toast.action != null && (
          <button
            type="button"
            className="liks-toast__action"
            onClick={() => {
              toast.action?.onClick();
              onDismiss();
            }}
          >
            {toast.action.label}
          </button>
        )}
      </div>

      <button
        type="button"
        className="liks-toast__dismiss"
        aria-label={dismissLabel}
        onClick={onDismiss}
      >
        <X className="liks-toast__dismiss-icon" strokeWidth={2} aria-hidden />
      </button>

      {!isPersistent ? (
        <div
          className="liks-toast__progress"
          aria-hidden
          onAnimationEnd={handleProgressAnimationEnd}
        />
      ) : null}
    </div>
  );
}

export { TOAST_EXIT_DURATION_MS };
